import React, { useEffect, useMemo } from "react";
import { Form, message } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import CommonDialog from "../../../../global/dialog";
import CommonForm from "../../../../global/form";
import SOURCES from "../../../../../utils/constants";
import { QUERY_KEYS } from "../../../../../lib/query-constants";
import { useGenericQuery } from "../../../../../queries/use-generic-query";
import { interceptor } from "../../../../../utils/interceptor";
import {
  formatAssignableLeadSourceOption,
  normalizeAssignableLeadSources,
} from "./staticResidentialIpUtils";

const EnhancedAddStaticResidentialIp = ({
  open,
  setOpen,
  mode = "add",
  editRecord = null,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const isAssign = mode === "assign";
  const isEdit = mode === "edit";
  const [loading, setLoading] = React.useState(false);
  const selectedPlatform = Form.useWatch("leadSource", form);

  const { data: leadSourceOptionsData } = useGenericQuery({
    endpoint: "leadsource/options",
    queryKey: QUERY_KEYS.leadSourceOptions.lists(),
    enabled: open && isAssign,
  });

  const assignPlatform = selectedPlatform || SOURCES.LEAD_KNOT;

  const { data: assignableLeadSourcesRaw = [], isLoading: assignableLeadSourcesLoading } = useGenericQuery({
    endpoint: "admin/proxies",
    queryKey: QUERY_KEYS.staticResidentialIpsAssignableLeadSources.lists({ leadSource: assignPlatform }),
    queryParams: {
      assignableLeadSources: "true",
      assignableUsers: "true",
      leadSource: assignPlatform,
    },
    enabled: open && isAssign,
  });

  const assignableLeadSources = useMemo(
    () => normalizeAssignableLeadSources(assignableLeadSourcesRaw),
    [assignableLeadSourcesRaw],
  );

  const platformOptions = useMemo(() => {
    if (Array.isArray(leadSourceOptionsData) && leadSourceOptionsData.length > 0) {
      return leadSourceOptionsData.map((item) => ({
        label: item.title,
        value: item.key,
      }));
    }
    return [];
  }, [leadSourceOptionsData]);

  const leadSourceRecordOptions = useMemo(
    () =>
      assignableLeadSources
        .map(formatAssignableLeadSourceOption)
        .filter((option) => option.value),
    [assignableLeadSources],
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    if (isAssign) {
      form.setFieldsValue({ leadSource: SOURCES.LEAD_KNOT });
    }
    if (isEdit && editRecord) {
      form.setFieldsValue({
        host: editRecord.host,
        port: editRecord.port,
        login: editRecord.login,
        password: "",
        type: editRecord.type || "http",
        provider: editRecord.provider || "webshare",
      });
    }
  }, [open, form, isAssign, isEdit, editRecord]);

  useEffect(() => {
    if (open && isAssign) {
      form.setFieldValue("leadSourceId", undefined);
    }
  }, [assignPlatform, open, isAssign, form]);

  const handleError = (err) => {
    message.error(err?.response?.data?.data?.error || err?.message || "Something went wrong");
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const axiosInstance = interceptor();

      if (isAssign) {
        await axiosInstance.post("admin/proxies/assign", {
          leadSourceId: values.leadSourceId,
        });
        await queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.staticResidentialIpsAssignableLeadSources.all,
        });
        message.success("Proxy assigned");
      } else if (isEdit) {
        const payload = {
          host: values.host,
          port: Number(values.port),
          login: values.login,
          type: values.type || "http",
          provider: values.provider || "webshare",
        };
        if (values.password) payload.password = values.password;
        await axiosInstance.patch(`admin/proxies/${editRecord._id}`, payload);
        message.success("Proxy updated");
      } else {
        await axiosInstance.post("admin/proxies", {
          host: values.host,
          port: Number(values.port),
          login: values.login,
          password: values.password,
          type: values.type || "http",
          provider: values.provider || "webshare",
        });
        message.success("Proxy added");
      }

      setOpen(false);
      form.resetFields();
      onSuccess?.();
    } catch (err) {
      if (err?.errorFields) return;
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const platformField = {
    label: "Platform",
    name: "leadSource",
    inputType: SOURCES.FORMINPUTTYPES.SELECT,
    placeholder: "Select platform",
    required: true,
    message: "Platform is required",
    options: platformOptions,
    span: 24,
  };

  const addFormData = [
    {
      label: "Host",
      name: "host",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "text",
      placeholder: "e.g. 1.2.3.4",
      required: true,
      message: "Host is required",
      span: 12,
    },
    {
      label: "Port",
      name: "port",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "number",
      placeholder: "e.g. 8080",
      required: true,
      message: "Port is required",
      span: 12,
    },
    {
      label: "Login",
      name: "login",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "text",
      placeholder: "Proxy username",
      required: true,
      message: "Login is required",
      span: 12,
    },
    {
      label: "Password",
      name: "password",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "password",
      placeholder: isEdit ? "Leave blank to keep current password" : "Proxy password",
      required: !isEdit,
      message: "Password is required",
      validate: false,
      span: 12,
    },
    {
      label: "Type",
      name: "type",
      inputType: SOURCES.FORMINPUTTYPES.SELECT,
      placeholder: "Select type",
      required: false,
      options: [
        { label: "HTTP", value: "http" },
        { label: "HTTPS", value: "https" },
        { label: "SOCKS5", value: "socks5" },
      ],
      span: 12,
    },
    {
      label: "Provider",
      name: "provider",
      inputType: SOURCES.FORMINPUTTYPES.INPUT,
      type: "text",
      placeholder: "webshare",
      required: false,
      span: 12,
    },
  ];

  const assignFormData = [
    platformField,
    {
      label: "Lead Source",
      name: "leadSourceId",
      inputType: SOURCES.FORMINPUTTYPES.SELECT,
      placeholder: assignableLeadSourcesLoading
        ? "Loading lead sources..."
        : leadSourceRecordOptions.length
          ? "Select lead source job"
          : "No eligible lead sources (all have an active proxy)",
      required: true,
      message: "Lead source is required",
      options: leadSourceRecordOptions,
      disabled: assignableLeadSourcesLoading || leadSourceRecordOptions.length === 0,
      loading: assignableLeadSourcesLoading,
      showSearch: true,
      optionFilterProp: "label",
      span: 24,
    },
  ];

  const title = isAssign ? "Assign Proxy" : isEdit ? "Edit Proxy" : "Add Proxy";
  const description = isAssign
    ? "Assign the next unassigned proxy from the pool to a lead source job."
    : isEdit
      ? "Update this static residential proxy."
      : "Add a static residential proxy to the pool.";

  return (
    <CommonDialog
      open={open}
      setOpen={setOpen}
      title={title}
      description={description}
      okButtonTitle={isAssign ? "Assign" : isEdit ? "Update" : "Add Proxy"}
      isConfirmButtonLoading={loading}
      onOk={handleSubmit}
      onCancel={() => {
        setOpen(false);
        form.resetFields();
      }}
    >
      <CommonForm form={form} data={isAssign ? assignFormData : addFormData} />
    </CommonDialog>
  );
};

export default EnhancedAddStaticResidentialIp;
